import React, { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QualificationWizard = ({ onClose, initialData = {} }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [wizardData, setWizardData] = useState({
    investor_roster: initialData.investor_roster || "",
    decision_rights: initialData.decision_rights || "",
    pof_files: initialData.pof_files || [],
    track_record: initialData.track_record || "",
    references: initialData.references || [
      { name: "", role: "", company: "", email: "", consent: false },
      { name: "", role: "", company: "", email: "", consent: false }
    ],
    thesis: initialData.thesis || "",
    jigsaw_fit: initialData.jigsaw_fit || "",
    plan_100d: initialData.plan_100d || "",
    plan_12m: initialData.plan_12m || "",
    perimeter: initialData.perimeter || "",
    structure: initialData.structure || {
      cash_pct: 0,
      vln_pct: 0,
      earnout_pct: 0,
      rollover_pct: 0,
      advisors: "",
      timeline_to_HOTs: "",
      timeline_to_completion: ""
    },
    accept_earnout: initialData.accept_earnout || false,
    accept_gdpr: initialData.accept_gdpr || false,
    slot: initialData.slot || "",
    additional_notes: initialData.additional_notes || ""
  });

  // Auto-save with debouncing (saves 2 seconds after user stops typing)
  const triggerAutoSave = () => {
    if (autoSaveTimer) {
      clearTimeout(autoSaveTimer);
    }
    const timer = setTimeout(() => {
      saveStep(currentStep);
    }, 2000); // 2 second debounce
    setAutoSaveTimer(timer);
  };

  // Cleanup auto-save timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimer) {
        clearTimeout(autoSaveTimer);
      }
    };
  }, [autoSaveTimer]);

  // Save when wizard closes
  useEffect(() => {
    return () => {
      // Save current step data when component unmounts
      saveStep(currentStep);
    };
  }, [currentStep]);

  const updateField = (field, value) => {
    setWizardData(prev => ({ ...prev, [field]: value }));
    // Trigger auto-save after field update
    triggerAutoSave();
  };

  const updateStructureField = (field, value) => {
    setWizardData(prev => ({
      ...prev,
      structure: { ...prev.structure, [field]: value }
    }));
    // Trigger auto-save after field update
    triggerAutoSave();
  };

  const updateReference = (index, field, value) => {
    triggerAutoSave(); // Trigger auto-save after reference update
    setWizardData(prev => {
      const refs = [...prev.references];
      refs[index] = { ...refs[index], [field]: value };
      return { ...prev, references: refs };
    });
  };

  const addPOFFile = () => {
    setWizardData(prev => ({
      ...prev,
      pof_files: [...prev.pof_files, { file_name: "", file_url: "", document_type: "", notes: "", uploaded_at: new Date().toISOString() }]
    }));
  };

  const updatePOFFile = (index, field, value) => {
    setWizardData(prev => {
      const files = [...prev.pof_files];
      files[index] = { ...files[index], [field]: value };
      return { ...prev, pof_files: files };
    });
    // Trigger auto-save after file update
    triggerAutoSave();
  };

  const saveStep = async (step) => {
    setSaving(true);
    try {
      const stepData = getStepData(step);
      await axios.patch(
        `${API}/submission/me/wizard`,
        { step, data: stepData },
        { withCredentials: true }
      );
      setLastSaved(new Date());
    } catch (err) {
      console.error("Autosave error:", err);
    } finally {
      setSaving(false);
    }
  };

  const getStepData = (step) => {
    switch (step) {
      case 1:
        return {
          investor_roster: wizardData.investor_roster,
          decision_rights: wizardData.decision_rights
        };
      case 2:
        return { pof_files: wizardData.pof_files };
      case 3:
        return {
          track_record: wizardData.track_record,
          references: wizardData.references
        };
      case 4:
        return {
          thesis: wizardData.thesis,
          jigsaw_fit: wizardData.jigsaw_fit
        };
      case 5:
        return {
          plan_100d: wizardData.plan_100d,
          plan_12m: wizardData.plan_12m
        };
      case 6:
        return {
          perimeter: wizardData.perimeter,
          structure: wizardData.structure
        };
      case 7:
        return {
          accept_earnout: wizardData.accept_earnout,
          accept_gdpr: wizardData.accept_gdpr,
          slot: wizardData.slot,
          additional_notes: wizardData.additional_notes
        };
      default:
        return {};
    }
  };

  const goToStep = async (step) => {
    await saveStep(currentStep);
    setCurrentStep(step);
  };

  const nextStep = async () => {
    await saveStep(currentStep);
    if (currentStep < 7) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white mb-4">Step 1: Investor Roster & Decision Rights</h3>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Investor Roster <span className="text-red-400">*</span>
              </label>
              <textarea
                data-testid="investor-roster"
                rows={4}
                className="w-full rounded-xl bg-slate-800/50 border border-slate-600 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                placeholder="Briefly list the main investors / capital providers backing this transaction."
                value={wizardData.investor_roster}
                onChange={(e) => updateField("investor_roster", e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Decision Rights <span className="text-red-400">*</span>
              </label>
              <textarea
                data-testid="decision-rights"
                rows={4}
                className="w-full rounded-xl bg-slate-800/50 border border-slate-600 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                placeholder="Who has final decision-making authority and what is the typical approval process?"
                value={wizardData.decision_rights}
                onChange={(e) => updateField("decision_rights", e.target.value)}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white mb-4">Step 2: Proof of Funds</h3>
            
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="text-blue-400 text-xl">ℹ️</div>
                <div className="text-sm text-slate-200 space-y-2">
                  <p className="font-semibold text-white">How to share your documents:</p>
                  <ul className="space-y-1 text-xs text-slate-300 list-disc list-inside">
                    <li>Upload recent proof of funds or LP commitment letters (within last 30 days)</li>
                    <li>Share via Google Drive, Dropbox, or similar cloud storage</li>
                    <li><strong className="text-white">Important:</strong> Grant view access to <span className="text-cyan-300 font-mono bg-slate-800/50 px-2 py-0.5 rounded">info@peakassetinvestments.com</span></li>
                    <li>Or provide a direct download URL if you're using secure file transfer</li>
                  </ul>
                </div>
              </div>
            </div>

            <p className="text-sm text-slate-300">
              Please provide at least one proof of funds document. <span className="text-red-400">*</span>
            </p>

            <div className="space-y-4">
              {wizardData.pof_files.map((file, idx) => (
                <div key={idx} className="bg-slate-800/30 rounded-xl p-5 space-y-4 border border-slate-700">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-white">Document {idx + 1}</h4>
                    {wizardData.pof_files.length > 1 && (
                      <button
                        onClick={() => {
                          const newFiles = wizardData.pof_files.filter((_, i) => i !== idx);
                          updateField("pof_files", newFiles);
                        }}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-2">
                      Document Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      className="w-full rounded-lg bg-slate-800/50 border border-slate-600 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                      placeholder="e.g., Bank Statement January 2025, Commitment Letter Q1 2025"
                      value={file.file_name}
                      onChange={(e) => updatePOFFile(idx, "file_name", e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-2">
                      Sharing Method <span className="text-red-400">*</span>
                    </label>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <input
                            type="text"
                            className="w-full rounded-lg bg-slate-800/50 border border-slate-600 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                            placeholder="Google Drive / Dropbox / OneDrive share link (with view access)"
                            value={file.file_url}
                            onChange={(e) => updatePOFFile(idx, "file_url", e.target.value)}
                          />
                        </div>
                      </div>
                      
                      <div className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
                        <p className="text-xs text-slate-400 mb-2">
                          <strong className="text-slate-300">✓ Recommended:</strong> Share via Google Drive or Dropbox
                        </p>
                        <ol className="text-xs text-slate-400 space-y-1 list-decimal list-inside">
                          <li>Upload your document to Google Drive / Dropbox</li>
                          <li>Right-click → Share → Add <span className="text-cyan-300 font-mono">info@peakassetinvestments.com</span></li>
                          <li>Set permission to "Viewer" or "Can view"</li>
                          <li>Copy the share link and paste it above</li>
                        </ol>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-2">
                      Document Type (optional)
                    </label>
                    <select
                      className="w-full rounded-lg bg-slate-800/50 border border-slate-600 px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-400"
                      value={file.document_type || ""}
                      onChange={(e) => updatePOFFile(idx, "document_type", e.target.value)}
                    >
                      <option value="">-- Select type --</option>
                      <option value="bank_statement">Bank Statement</option>
                      <option value="commitment_letter">LP Commitment Letter</option>
                      <option value="fund_statement">Fund Statement</option>
                      <option value="letter_of_credit">Letter of Credit</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-2">
                      Additional Notes (optional)
                    </label>
                    <textarea
                      rows={2}
                      className="w-full rounded-lg bg-slate-800/50 border border-slate-600 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-400"
                      placeholder="Any additional context about this document..."
                      value={file.notes || ""}
                      onChange={(e) => updatePOFFile(idx, "notes", e.target.value)}
                    />
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={addPOFFile}
              className="w-full py-3 rounded-xl border-2 border-dashed border-slate-600 hover:border-cyan-500 text-cyan-400 hover:text-cyan-300 font-medium transition"
            >
              + Add Another Document
            </button>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white mb-4">Step 3: Track Record & References</h3>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Track Record <span className="text-red-400">*</span>
              </label>
              <textarea
                data-testid="track-record"
                rows={5}
                className="w-full rounded-xl bg-slate-800/50 border border-slate-600 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                placeholder="Summarise your track record in acquisitions or investments in services businesses."
                value={wizardData.track_record}
                onChange={(e) => updateField("track_record", e.target.value)}
              />
            </div>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-slate-300">
                References (minimum 2) <span className="text-red-400">*</span>
              </label>
              
              {wizardData.references.map((ref, idx) => (
                <div key={idx} className="bg-slate-800/30 rounded-xl p-4 space-y-3">
                  <div className="text-xs font-semibold text-slate-400 mb-2">Reference {idx + 1}</div>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      className="rounded-lg bg-slate-800/50 border border-slate-600 px-3 py-2 text-sm text-white"
                      placeholder="Name"
                      value={ref.name}
                      onChange={(e) => updateReference(idx, "name", e.target.value)}
                    />
                    <input
                      type="text"
                      className="rounded-lg bg-slate-800/50 border border-slate-600 px-3 py-2 text-sm text-white"
                      placeholder="Role"
                      value={ref.role}
                      onChange={(e) => updateReference(idx, "role", e.target.value)}
                    />
                    <input
                      type="text"
                      className="rounded-lg bg-slate-800/50 border border-slate-600 px-3 py-2 text-sm text-white"
                      placeholder="Company"
                      value={ref.company}
                      onChange={(e) => updateReference(idx, "company", e.target.value)}
                    />
                    <input
                      type="email"
                      className="rounded-lg bg-slate-800/50 border border-slate-600 px-3 py-2 text-sm text-white"
                      placeholder="Email"
                      value={ref.email}
                      onChange={(e) => updateReference(idx, "email", e.target.value)}
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-slate-300">
                    <input
                      type="checkbox"
                      checked={ref.consent}
                      onChange={(e) => updateReference(idx, "consent", e.target.checked)}
                      className="rounded"
                    />
                    I have consent to provide this reference
                  </label>
                </div>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white mb-4">Step 4: Thesis & Jigsaw Fit</h3>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Investment Thesis <span className="text-red-400">*</span>
              </label>
              <textarea
                data-testid="thesis"
                rows={5}
                className="w-full rounded-xl bg-slate-800/50 border border-slate-600 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                placeholder="What is your investment thesis for Jigsaw Conferences specifically?"
                value={wizardData.thesis}
                onChange={(e) => updateField("thesis", e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Jigsaw Fit <span className="text-red-400">*</span>
              </label>
              <textarea
                data-testid="jigsaw-fit"
                rows={5}
                className="w-full rounded-xl bg-slate-800/50 border border-slate-600 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                placeholder="Where do you see the strongest value-creation levers in the Jigsaw / corporate accommodation space?"
                value={wizardData.jigsaw_fit}
                onChange={(e) => updateField("jigsaw_fit", e.target.value)}
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white mb-4">Step 5: Operator Plans</h3>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                First 100 Days Plan <span className="text-red-400">*</span>
              </label>
              <textarea
                data-testid="plan-100d"
                rows={5}
                className="w-full rounded-xl bg-slate-800/50 border border-slate-600 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                placeholder="Outline your first 100 days post-completion (client continuity, staff, key changes)."
                value={wizardData.plan_100d}
                onChange={(e) => updateField("plan_100d", e.target.value)}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                12-Month Plan <span className="text-red-400">*</span>
              </label>
              <textarea
                data-testid="plan-12m"
                rows={5}
                className="w-full rounded-xl bg-slate-800/50 border border-slate-600 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 transition"
                placeholder="Outline your 12-month plan (growth, investment, team, systems)."
                value={wizardData.plan_12m}
                onChange={(e) => updateField("plan_12m", e.target.value)}
              />
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white mb-4">Step 6: Perimeter & Structure</h3>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-3">
                Transaction Perimeter <span className="text-red-400">*</span>
              </label>
              <div className="space-y-2">
                <label className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-lg cursor-pointer hover:bg-slate-800/50">
                  <input
                    type="radio"
                    name="perimeter"
                    value="OpCoViaHoldCo"
                    checked={wizardData.perimeter === "OpCoViaHoldCo"}
                    onChange={(e) => updateField("perimeter", e.target.value)}
                    className="mt-1"
                  />
                  <div className="text-sm text-slate-200">
                    Share purchase of Jigsaw Conferences Ltd via Jigsaw Property Holding Ltd.
                  </div>
                </label>
                <label className="flex items-start gap-3 p-3 bg-slate-800/30 rounded-lg cursor-pointer hover:bg-slate-800/50">
                  <input
                    type="radio"
                    name="perimeter"
                    value="HoldCoSale"
                    checked={wizardData.perimeter === "HoldCoSale"}
                    onChange={(e) => updateField("perimeter", e.target.value)}
                    className="mt-1"
                  />
                  <div className="text-sm text-slate-200">
                    Share purchase of Jigsaw Property Holding Ltd.
                  </div>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-3">
                Deal Structure (%) <span className="text-red-400">*</span>
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Cash %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="w-full rounded-lg bg-slate-800/50 border border-slate-600 px-3 py-2 text-sm text-white"
                    value={wizardData.structure.cash_pct}
                    onChange={(e) => updateStructureField("cash_pct", parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">VLN %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="w-full rounded-lg bg-slate-800/50 border border-slate-600 px-3 py-2 text-sm text-white"
                    value={wizardData.structure.vln_pct}
                    onChange={(e) => updateStructureField("vln_pct", parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Earn-out %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="w-full rounded-lg bg-slate-800/50 border border-slate-600 px-3 py-2 text-sm text-white"
                    value={wizardData.structure.earnout_pct}
                    onChange={(e) => updateStructureField("earnout_pct", parseFloat(e.target.value) || 0)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Rollover %</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    className="w-full rounded-lg bg-slate-800/50 border border-slate-600 px-3 py-2 text-sm text-white"
                    value={wizardData.structure.rollover_pct}
                    onChange={(e) => updateStructureField("rollover_pct", parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
              <div className="text-xs text-slate-400 mt-2">
                Total: {(wizardData.structure.cash_pct + wizardData.structure.vln_pct + wizardData.structure.earnout_pct + wizardData.structure.rollover_pct).toFixed(0)}%
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Advisors <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                className="w-full rounded-xl bg-slate-800/50 border border-slate-600 px-4 py-3 text-sm text-white"
                placeholder="Intended legal and financial advisors for this transaction"
                value={wizardData.structure.advisors}
                onChange={(e) => updateStructureField("advisors", e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Timeline to HOTs <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  className="w-full rounded-xl bg-slate-800/50 border border-slate-600 px-4 py-3 text-sm text-white"
                  placeholder="e.g., 2-3 weeks"
                  value={wizardData.structure.timeline_to_HOTs}
                  onChange={(e) => updateStructureField("timeline_to_HOTs", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Timeline to Completion <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  className="w-full rounded-xl bg-slate-800/50 border border-slate-600 px-4 py-3 text-sm text-white"
                  placeholder="e.g., 8-12 weeks from HOTs"
                  value={wizardData.structure.timeline_to_completion}
                  onChange={(e) => updateStructureField("timeline_to_completion", e.target.value)}
                />
              </div>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-white mb-4">Step 7: Acceptance & Schedule</h3>
            
            <div className="space-y-4">
              <label className="flex items-start gap-3 p-4 bg-slate-800/30 rounded-xl cursor-pointer">
                <input
                  type="checkbox"
                  checked={wizardData.accept_earnout}
                  onChange={(e) => updateField("accept_earnout", e.target.checked)}
                  className="mt-1"
                />
                <div className="text-sm text-slate-200">
                  I understand that part of the consideration may be structured as an earn-out tied to recurring corporate accommodation performance. <span className="text-red-400">*</span>
                </div>
              </label>

              <label className="flex items-start gap-3 p-4 bg-slate-800/30 rounded-xl cursor-pointer">
                <input
                  type="checkbox"
                  checked={wizardData.accept_gdpr}
                  onChange={(e) => updateField("accept_gdpr", e.target.checked)}
                  className="mt-1"
                />
                <div className="text-sm text-slate-200">
                  I agree that my details may be stored securely by Jigsaw Conferences Ltd for the purpose of this potential transaction. <span className="text-red-400">*</span>
                </div>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Select Provisional Meeting Slot <span className="text-red-400">*</span>
              </label>
              <input
                type="datetime-local"
                data-testid="slot-select"
                className="w-full rounded-xl bg-slate-800/50 border border-slate-600 px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
                value={wizardData.slot ? new Date(wizardData.slot).toISOString().slice(0, 16) : ""}
                onChange={(e) => {
                  const localDateTime = e.target.value;
                  if (localDateTime) {
                    // Convert local datetime to ISO string for storage
                    const isoString = new Date(localDateTime).toISOString();
                    updateField("slot", isoString);
                  } else {
                    updateField("slot", "");
                  }
                }}
                min={new Date().toISOString().slice(0, 16)}
              />
              <p className="text-xs text-slate-400 mt-2">
                Select your preferred date and time for the meeting (UK time)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Additional Notes or Comments (optional)
              </label>
              <textarea
                rows={3}
                className="w-full rounded-xl bg-slate-800/50 border border-slate-600 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:border-transparent"
                placeholder="Any additional context or comments you'd like to share..."
                value={wizardData.additional_notes || ""}
                onChange={(e) => updateField("additional_notes", e.target.value)}
              />
            </div>

            {/* GDPR Notice */}
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-5 space-y-3">
              <div className="flex items-start gap-3">
                <div className="text-blue-400 text-xl flex-shrink-0">🔒</div>
                <div className="text-xs text-slate-200 space-y-3">
                  <p className="font-semibold text-white text-sm">Data Protection & Privacy</p>
                  
                  <p>
                    <strong>Jigsaw Conferences Ltd</strong> processes the information you provide in this Deal Room for the purpose of 
                    evaluating a potential transaction and managing our internal decision-making. Our lawful basis is <strong>legitimate 
                    interests</strong> and taking steps at your request prior to entering into a contract.
                  </p>
                  
                  <p>
                    We retain this data for up to <strong>24 months</strong> unless required longer for legal or regulatory reasons. 
                    You can request access, correction or deletion of your data as described in our{" "}
                    <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" className="text-cyan-300 hover:text-cyan-200 underline">
                      Privacy Notice
                    </a>.
                  </p>
                  
                  <p>
                    <strong>References:</strong> By providing referee details, you confirm that those individuals have consented to 
                    their information being shared with us for the purpose of referencing.
                  </p>
                  
                  <p className="border-t border-blue-500/20 pt-3 mt-3">
                    <strong>Data protection queries:</strong>{" "}
                    <a href="mailto:privacy@jigsawconferences.co.uk" className="text-cyan-300 hover:text-cyan-200 underline">
                      privacy@jigsawconferences.co.uk
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden border border-slate-700 shadow-2xl">
        {/* Header */}
        <div className="bg-slate-800/50 border-b border-slate-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-white">Qualification Wizard</h2>
            <button
              onClick={async () => {
                // Save before closing
                await saveStep(currentStep);
                onClose();
              }}
              className="text-slate-400 hover:text-white text-2xl leading-none"
            >
              ×
            </button>
          </div>
          
          {/* Progress bar */}
          <div className="flex items-center gap-2">
            {[1, 2, 3, 4, 5, 6, 7].map((step) => (
              <div
                key={step}
                onClick={() => goToStep(step)}
                className={`flex-1 h-2 rounded-full cursor-pointer transition ${
                  step <= currentStep ? "bg-cyan-500" : "bg-slate-700"
                }`}
              />
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between text-sm text-slate-400">
            <span>Step {currentStep} of 7</span>
            <span>
              {saving ? (
                <span className="text-cyan-400">💾 Saving...</span>
              ) : lastSaved ? (
                <span className="text-green-400">✓ Saved {new Date(lastSaved).toLocaleTimeString()}</span>
              ) : null}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {renderStep()}
        </div>

        {/* Footer */}
        <div className="bg-slate-800/50 border-t border-slate-700 p-6 flex items-center justify-between">
          <button
            onClick={prevStep}
            disabled={currentStep === 1}
            className="px-6 py-2 rounded-xl bg-slate-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-600 transition"
          >
            Previous
          </button>
          
          <button
            onClick={nextStep}
            disabled={currentStep === 7}
            className="px-6 py-2 rounded-xl bg-cyan-500 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-cyan-600 transition"
          >
            {currentStep === 7 ? "Complete" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default QualificationWizard;
